import { useEffect, useState } from 'react';
import {
  IClienteAcessoAuditoria,
  TIPOS_ACESSO_AUDITORIA,
  TipoAcessoAuditoria,
} from '../../interfaces/IClienteAcessoAuditoria';
import ClienteAcessoAuditoriaService from '../../services/ClienteAcessoAuditoriaService';

interface Props {
  clienteId: number;
  ativo: boolean;
}

function formatarDataHora(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function badgeTier(tier: TipoAcessoAuditoria) {
  const map: Record<TipoAcessoAuditoria, { cls: string; label: string }> = {
    Nenhum:      { cls: 'badge-light-secondary', label: 'Sem acesso' },
    Diagnostico: { cls: 'badge-light-info',      label: 'Diagnóstico' },
    Trial72h:    { cls: 'badge-light-warning',   label: 'Trial 72 h' },
    FullPremium: { cls: 'badge-light-success',   label: 'Full Premium' },
    Enterprise:  { cls: 'badge-light-primary',   label: 'Enterprise' },
  };
  const { cls, label } = map[tier] ?? map.Nenhum;
  return <span className={`badge ${cls}`}>{label}</span>;
}

const ClientesAuditoriaTab = ({ clienteId, ativo }: Props) => {
  const [acesso, setAcesso] = useState<IClienteAcessoAuditoria | null>(null);
  const [statusSelecionado, setStatusSelecionado] = useState<TipoAcessoAuditoria>('Nenhum');
  const [salvando, setSalvando] = useState(false);
  const [liberandoTrial, setLiberandoTrial] = useState(false);
  const [gerandoChave, setGerandoChave] = useState(false);
  const [apiChaveCopiada, setApiChaveCopiada] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!ativo || clienteId <= 0) return;
    ClienteAcessoAuditoriaService.getAcesso(clienteId)
      .then(r => {
        setAcesso(r.data);
        setStatusSelecionado(r.data.statusAcesso as TipoAcessoAuditoria);
      })
      .catch(() => setErro('Não foi possível carregar as configurações de auditoria.'));
  }, [ativo, clienteId]);

  async function salvarStatus() {
    if (clienteId <= 0) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await ClienteAcessoAuditoriaService.setStatus(clienteId, { statusAcesso: statusSelecionado });
      setAcesso(r.data);
    } catch {
      setErro('Erro ao salvar o status de acesso.');
    } finally {
      setSalvando(false);
    }
  }

  async function liberarTrial() {
    if (clienteId <= 0) return;
    setLiberandoTrial(true);
    setErro(null);
    try {
      const r = await ClienteAcessoAuditoriaService.liberarTrial(clienteId);
      setAcesso(r.data);
      setStatusSelecionado(r.data.statusAcesso as TipoAcessoAuditoria);
    } catch {
      setErro('Erro ao liberar trial.');
    } finally {
      setLiberandoTrial(false);
    }
  }

  async function gerarApiKey() {
    if (clienteId <= 0) return;
    setGerandoChave(true);
    setErro(null);
    try {
      const r = await ClienteAcessoAuditoriaService.gerarApiKey(clienteId);
      setAcesso(prev => prev ? { ...prev, apiChave: r.data.apiChave, flagEnterprise: true, statusAcesso: 'Enterprise', statusAcessoEfetivo: 'Enterprise' } : prev);
      setStatusSelecionado('Enterprise');
    } catch {
      setErro('Erro ao gerar API Key.');
    } finally {
      setGerandoChave(false);
    }
  }

  function copiarChave(chave: string) {
    navigator.clipboard.writeText(chave).then(() => {
      setApiChaveCopiada(true);
      setTimeout(() => setApiChaveCopiada(false), 2500);
    });
  }

  if (!ativo) return null;

  return (
    <div style={{ padding: '10px' }}>
      <div className='card card-custom card-stretch'>
        <div className='card-header border-0 pt-4'>
          <h4 className='card-title fw-bolder text-dark'>Controle de Acesso — Revisor Contábil</h4>
        </div>

        <div className='card-body pt-2'>
          {erro && (
            <div className='alert alert-danger py-2 px-4 mb-4'>{erro}</div>
          )}

          {acesso && (
            <>
              {/* Status Atual */}
              <div className='row g-3 mb-5'>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Status Configurado</div>
                    <div>{badgeTier(acesso.statusAcesso as TipoAcessoAuditoria)}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Status Efetivo (agora)</div>
                    <div>{badgeTier(acesso.statusAcessoEfetivo as TipoAcessoAuditoria)}</div>
                    {acesso.trialExpirado && (
                      <div className='text-danger fs-8 mt-1'>Trial expirado — revertido para Diagnóstico</div>
                    )}
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Visibilidade dos Resultados</div>
                    <div className='fw-bolder fs-4'>{acesso.percentualVisualizacao}%</div>
                  </div>
                </div>
              </div>

              {/* Datas do Trial */}
              {(acesso.dataInicioTrial || acesso.dataExpiracaoTrial) && (
                <div className='row g-3 mb-5'>
                  <div className='col-md-6'>
                    <label className='form-label text-muted fs-8'>Início do Trial</label>
                    <div className='form-control bg-light'>{formatarDataHora(acesso.dataInicioTrial)}</div>
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label text-muted fs-8'>Expiração do Trial</label>
                    <div className={`form-control bg-light ${acesso.trialExpirado ? 'text-danger fw-bold' : ''}`}>
                      {formatarDataHora(acesso.dataExpiracaoTrial)}
                    </div>
                  </div>
                </div>
              )}

              {/* Alterar Status */}
              <div className='separator my-4' />
              <h6 className='fw-bold mb-3'>Alterar Status de Acesso</h6>
              <div className='row g-3 align-items-end mb-4'>
                <div className='col-md-8'>
                  <label className='form-label required'>Novo Status</label>
                  <select
                    className='form-select'
                    value={statusSelecionado}
                    onChange={e => setStatusSelecionado(e.target.value as TipoAcessoAuditoria)}
                  >
                    {TIPOS_ACESSO_AUDITORIA
                      .filter(opt => opt.value !== 'Trial72h')
                      .map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                  </select>
                </div>
                <div className='col-md-4'>
                  <button
                    type='button'
                    className='btn btn-primary w-100'
                    disabled={salvando || statusSelecionado === acesso.statusAcesso}
                    onClick={salvarStatus}
                  >
                    {salvando ? (
                      <span className='spinner-border spinner-border-sm me-2' />
                    ) : (
                      <i className='bi bi-check-circle me-2' />
                    )}
                    Salvar Status
                  </button>
                </div>
              </div>

              {/* Botão Liberar Trial */}
              <div className='separator my-4' />
              <h6 className='fw-bold mb-2'>Trial de 72 Horas</h6>
              <p className='text-muted fs-8 mb-3'>
                Libera acesso à Camada 2 (20% das ocorrências) por 72 horas. Ao expirar,
                o sistema reverte automaticamente para Diagnóstico e notifica os usuários por e-mail.
              </p>
              <button
                type='button'
                className='btn btn-warning'
                disabled={liberandoTrial}
                onClick={liberarTrial}
              >
                {liberandoTrial ? (
                  <span className='spinner-border spinner-border-sm me-2' />
                ) : (
                  <i className='bi bi-clock-history me-2' />
                )}
                Liberar Trial (72 h)
              </button>

              {/* Enterprise API Key */}
              <div className='separator my-4' />
              <h6 className='fw-bold mb-2'>Chave de API — Enterprise</h6>
              <p className='text-muted fs-8 mb-3'>
                Gera a chave <code>x-api-key</code> para integração programática. Clientes Enterprise
                têm acesso total (Camada 3) via API, sem necessidade de login Firebase.
              </p>

              {acesso.apiChave && (
                <div className='input-group mb-3'>
                  <input
                    readOnly
                    type='text'
                    className='form-control font-monospace fs-8'
                    value={acesso.apiChave}
                  />
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => copiarChave(acesso.apiChave!)}
                  >
                    {apiChaveCopiada ? (
                      <><i className='bi bi-check-circle text-success me-1' />Copiado</>
                    ) : (
                      <><i className='bi bi-clipboard me-1' />Copiar</>
                    )}
                  </button>
                </div>
              )}

              <button
                type='button'
                className='btn btn-primary'
                disabled={gerandoChave}
                onClick={gerarApiKey}
              >
                {gerandoChave ? (
                  <span className='spinner-border spinner-border-sm me-2' />
                ) : (
                  <i className='bi bi-key me-2' />
                )}
                {acesso.apiChave ? 'Regenerar API Key' : 'Gerar API Key Enterprise'}
              </button>
            </>
          )}

          {!acesso && !erro && (
            <div className='text-center py-8'>
              <span className='spinner-border text-primary' />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientesAuditoriaTab;
